/**
 * «Планирование»: чистая арифметика калькулятора и хранение черновика допущений.
 *
 * Без React и без сети. Площадь, окно и подача воды приходят из канона
 * (агроцикл, его план, файл ирригации) — их сюда передаёт экран. Технику,
 * производительность, норму воды, затраты и бюджет задаёт человек: в базе района
 * таких сущностей нет, поэтому они хранятся только в этом браузере и подписаны
 * «черновик» (решение №1871).
 */

/** Допущения пользователя — единственное, что экран пишет в `localStorage`. */
export interface Assumptions {
  /** Сколько агрегатов (единиц техники) выделено на работу. */
  units: number;
  /** Сколько гектаров в день обрабатывает один агрегат. */
  haPerUnitDay: number;
  /** Норма воды, м³ на гектар, для поливных работ. */
  waterPerHa: number;
  /** Затраты на гектар выбранной работы, сум. */
  costPerHa: number;
  /** Доступный бюджет, сум. */
  budget: number;
  /** Площадь, введённая руками, по агроциклу: нужна, когда в каноне земли ещё нет (будущий сезон). */
  areaByCycle: Record<string, number>;
}

export const DEFAULTS: Assumptions = {
  units: 10,
  haPerUnitDay: 12,
  waterPerHa: 900,
  costPerHa: 1_500_000,
  budget: 2_500_000_000,
  areaByCycle: {},
};

export const STORAGE_KEY = 'astra-planning-draft-v2';

/** Границы полей: за ними ввод считается опечаткой и не принимается. */
export const LIMITS = {
  units: { min: 1, max: 10_000, integer: true },
  haPerUnitDay: { min: 0.1, max: 1_000, integer: false },
  waterPerHa: { min: 0, max: 100_000, integer: false },
  costPerHa: { min: 0, max: 1e9, integer: false },
  budget: { min: 0, max: 1e13, integer: false },
  area: { min: 1, max: 1_000_000, integer: false },
} as const;

export type NumericField = keyof typeof LIMITS;

/** Число в пределах поля или `null`: пустое, не число и выход за границы не принимаются. */
export function parseField(field: NumericField, raw: string): number | null {
  const text = raw.trim().replace(/\s+/g, '').replace(',', '.');
  if (text === '') return null;
  const n = Number(text);
  const { min, max, integer } = LIMITS[field];
  if (!Number.isFinite(n) || n < min || n > max) return null;
  return integer ? Math.floor(n) : n;
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** Допущения из сохранённой строки; всё, что не прошло проверку, — значение по умолчанию. */
export function parseDraft(raw: string | null): Assumptions {
  if (!raw) return { ...DEFAULTS, areaByCycle: {} };
  try {
    const value: unknown = JSON.parse(raw);
    if (!isRecord(value)) return { ...DEFAULTS, areaByCycle: {} };
    const pick = (field: Exclude<NumericField, 'area'>): number => {
      const v = value[field];
      return typeof v === 'number' && parseField(field, String(v)) === v ? v : DEFAULTS[field];
    };
    const areaByCycle: Record<string, number> = {};
    if (isRecord(value.areaByCycle)) {
      for (const [key, v] of Object.entries(value.areaByCycle)) {
        if (typeof v === 'number' && parseField('area', String(v)) === v) areaByCycle[key] = v;
      }
    }
    return {
      units: pick('units'),
      haPerUnitDay: pick('haPerUnitDay'),
      waterPerHa: pick('waterPerHa'),
      costPerHa: pick('costPerHa'),
      budget: pick('budget'),
      areaByCycle,
    };
  } catch {
    return { ...DEFAULTS, areaByCycle: {} };
  }
}

/** Прочитать черновик; браузер без хранилища — значения по умолчанию. */
export function readDraft(): Assumptions {
  try {
    return parseDraft(localStorage.getItem(STORAGE_KEY));
  } catch {
    return { ...DEFAULTS, areaByCycle: {} };
  }
}

/** Записать черновик; `false` — браузер не дал (приватный режим, переполнение). */
export function writeDraft(draft: Assumptions): boolean {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
    return true;
  } catch {
    return false;
  }
}

/** Календарные дни окна, обе границы включены; `null` — окно не задано или перевёрнуто. */
export function windowDays(from: string | null | undefined, to: string | null | undefined): number | null {
  if (!from || !to) return null;
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return null;
  return Math.round((b - a) / 86_400_000) + 1;
}

export interface CalcInput {
  areaHa: number;
  windowDays: number;
  units: number;
  haPerUnitDay: number;
  waterPerHa: number;
  costPerHa: number;
  budget: number;
  /** Поливная ли работа: у остальных воды на гектар нет. */
  irrigation: boolean;
}

export interface CalcResult {
  /** Дневная выработка всех агрегатов, га/день. */
  dailyHa: number;
  /** Сколько дней нужно на всю площадь. */
  requiredDays: number;
  /** Дни окна минус нужные дни; меньше нуля — не успеваем. */
  reserveDays: number;
  /** Сколько агрегатов нужно, чтобы уложиться в окно. */
  requiredUnits: number;
  /** Агрегатов не хватает (0 — хватает). */
  unitsShort: number;
  /** Сколько гектаров успеют обработать заданные агрегаты за окно. */
  capacityHa: number;
  /** Гектары, которые в окно не уложатся. */
  uncoveredHa: number;
  /** Воды нужно, м³; `null` — работа не поливная. */
  waterM3: number | null;
  /** Затраты, сум. */
  costSum: number;
  /** Бюджета не хватает, сум (0 — хватает). */
  budgetShort: number;
  /** Запас бюджета, сум (может быть отрицательным). */
  budgetReserve: number;
}

/** Расчёт «хватит ли»; `null`, если входы не позволяют считать (нет площади или окна). */
export function calculate(input: CalcInput): CalcResult | null {
  const { areaHa, windowDays: days, units, haPerUnitDay, waterPerHa, costPerHa, budget, irrigation } = input;
  const ok = [areaHa, days, units, haPerUnitDay, waterPerHa, costPerHa, budget].every((v) => Number.isFinite(v) && v >= 0);
  if (!ok || areaHa <= 0 || days < 1 || units < 1 || haPerUnitDay <= 0) return null;
  const dailyHa = units * haPerUnitDay;
  const requiredDays = Math.ceil(areaHa / dailyHa);
  const requiredUnits = Math.ceil(areaHa / (haPerUnitDay * days));
  const capacityHa = dailyHa * days;
  const costSum = areaHa * costPerHa;
  return {
    dailyHa,
    requiredDays,
    reserveDays: days - requiredDays,
    requiredUnits,
    unitsShort: Math.max(0, requiredUnits - units),
    capacityHa,
    uncoveredHa: Math.max(0, areaHa - capacityHa),
    waterM3: irrigation ? areaHa * waterPerHa : null,
    costSum,
    budgetShort: Math.max(0, costSum - budget),
    budgetReserve: budget - costSum,
  };
}

/* ------------------------------------------------------------ выбор по умолчанию */

export interface CycleChoiceRow {
  key: string;
  crop_code: string;
  status: string;
  plan_from?: string | null;
  participants?: { land_ha?: number | null } | null;
  kpis?: Record<string, { value?: number | null } | undefined> | null;
}

/**
 * Агроцикл по умолчанию: идущий хлопок, затем любой идущий, затем тот, где
 * есть земля, иначе первый. «Идущий» — `started`; строки без плана не берём
 * первыми, когда есть из чего выбирать.
 */
export function pickDefaultCycle(rows: readonly CycleChoiceRow[]): string | null {
  const planned = (r: CycleChoiceRow) => (r.kpis?.['plan.works']?.value ?? 0) > 0;
  const usable = rows.filter(planned);
  const pool = usable.length ? usable : rows;
  return (
    pool.find((r) => r.status === 'started' && r.crop_code === 'COTTON') ??
    pool.find((r) => r.status === 'started') ??
    pool.find((r) => (r.participants?.land_ha ?? 0) > 0) ??
    pool[0]
  )?.key ?? null;
}

export interface WorkChoiceRow {
  id: number;
  window_from: string | null;
  window_to: string | null;
  upcoming?: string | null;
  campaign_status?: string | null;
}

/**
 * Работа по умолчанию — та, под которую планируют: ближайшая впереди (окно ещё
 * не открылось), иначе первая с открытым окном, у которой кампания не закрыта,
 * иначе последняя в плане. `today` — дата среза.
 */
export function pickDefaultWork(works: readonly WorkChoiceRow[], today: string): number | null {
  if (!works.length) return null;
  const ahead = works
    .filter((w) => w.window_from && w.window_from > today)
    .sort((a, b) => (a.window_from! < b.window_from! ? -1 : a.window_from! > b.window_from! ? 1 : 0));
  if (ahead.length) return ahead[0]!.id;
  const open = works.find(
    (w) => w.window_from && w.window_to && w.window_from <= today && today <= w.window_to && w.campaign_status !== 'completed',
  );
  return (open ?? works[works.length - 1])!.id;
}
